import { getIn, useFormikContext } from "formik"
import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { LabeledField } from "../../../../../../shared/components/forms/LabeledField"

import { MenuTemplateFormValues } from "../menuTemplatesTypes"
import { TemplateCategoryBlock } from "./TemplateCategoryBlock"
import { TemplateChannelPicker } from "./TemplateChannelPicker"

const useStyles = makeStyles()((theme) => ({
    section: {
        padding: "1.5rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
    },
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    onEditCategoryName: (categoryIndex: number | null) => void
    onRemoveCategory: (categoryIndex: number) => void
    onAddDish: (categoryIndex: number) => void
    onOpenDish: (categoryIndex: number, dishIndex: number) => void
    onRemoveDish: (categoryIndex: number, dishIndex: number) => void
}>

export function TemplateStructureForm({
    onEditCategoryName,
    onRemoveCategory,
    onAddDish,
    onOpenDish,
    onRemoveDish,
}: Props) {
    const { classes } = useStyles()
    const { values, errors, submitCount } = useFormikContext<MenuTemplateFormValues>()

    // Marks invalid dishes on their cards after a save attempt — their errors live inside a closed drill-in.
    const dishHasError = (categoryIndex: number, dishIndex: number) =>
        submitCount > 0 && Boolean(getIn(errors, `categories[${categoryIndex}].dishes[${dishIndex}]`))

    return (
        <Stack spacing="1.5rem">
            <Stack spacing="1.5rem" className={classes.section}>
                <Stack spacing=".25rem">
                    <LabeledField
                        label={
                            <LocalizedStrict id="merchant-menu-templates-editor-name-label">
                                <span>Template name</span>
                            </LocalizedStrict>
                        }
                    >
                        <LocalizedStrict id="merchant-menu-templates-editor-name-field" attrs={{ placeholder: true }}>
                            <FormikTextField name="name" placeholder="Template name" fullWidth />
                        </LocalizedStrict>
                    </LabeledField>
                    <LocalizedStrict id="merchant-menu-templates-editor-name-hint">
                        <Typography variant="caption" className={classes.hint}>
                            Internal name — not shown to customers.
                        </Typography>
                    </LocalizedStrict>
                </Stack>

                <Stack spacing=".5rem">
                    <LocalizedStrict id="merchant-menu-templates-editor-channels-label">
                        <Typography variant="subtitle1">Published to</Typography>
                    </LocalizedStrict>
                    <TemplateChannelPicker />
                    <LocalizedStrict id="merchant-menu-templates-editor-channels-hint">
                        <Typography variant="caption" className={classes.hint}>
                            Where this template's menu is shown.
                        </Typography>
                    </LocalizedStrict>
                </Stack>
            </Stack>

            <Stack spacing=".75rem">
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing="1rem">
                    <LocalizedStrict id="merchant-menu-templates-editor-categories-title">
                        <Typography variant="h6">Categories</Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-menu-templates-editor-add-category-button">
                        <StandardButton
                            fullWidth={false}
                            size="medium"
                            variant="outlined"
                            onClick={() => onEditCategoryName(null)}
                        >
                            Add category
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>

                <Stack spacing="1rem">
                    {values.categories.map((category, categoryIndex) => (
                        <TemplateCategoryBlock
                            key={categoryIndex}
                            category={category}
                            dishErrorFlags={category.dishes.map((_, dishIndex) =>
                                dishHasError(categoryIndex, dishIndex),
                            )}
                            onEditName={() => onEditCategoryName(categoryIndex)}
                            onRemove={() => onRemoveCategory(categoryIndex)}
                            onAddDish={() => onAddDish(categoryIndex)}
                            onOpenDish={(dishIndex) => onOpenDish(categoryIndex, dishIndex)}
                            onRemoveDish={(dishIndex) => onRemoveDish(categoryIndex, dishIndex)}
                        />
                    ))}
                </Stack>
            </Stack>
        </Stack>
    )
}
